import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import TrainerSidebar from '../../components/ui/TrainerSidebar';
import SessionMonitorHeader from '../../components/ui/SessionMonitorHeader';
import ExerciseDatabase from './components/ExerciseDatabase';
import PlanBuilder from './components/PlanBuilder';
import PlanPreview from './components/PlanPreview';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';

const TrainingPlanBuilder = () => {
  const navigate = useNavigate();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [showPreview, setShowPreview] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [activeTab, setActiveTab] = useState('exercises'); // exercises, builder

  const [planData, setPlanData] = useState({
    id: null,
    name: '',
    description: '',
    assignedClient: '',
    defaultPhase: 'phase-1',
    autoProgression: true,
    adaptiveIntensity: true,
    exercises: [],
    createdAt: new Date(),
    updatedAt: new Date()
  });

  // Check for mobile viewport
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1024);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const handleExerciseUpdate = (exerciseId, updatedExercise) => {
    setPlanData(prev => ({
      ...prev,
      exercises: prev?.exercises?.map(ex => 
        ex?.id === exerciseId ? updatedExercise : ex
      ),
      updatedAt: new Date()
    }));
  };

  const handleExerciseRemove = (exerciseId) => {
    setPlanData(prev => ({
      ...prev,
      exercises: prev?.exercises?.filter(ex => ex?.id !== exerciseId),
      updatedAt: new Date()
    }));
  };

  const handleSupersetCreate = (exerciseId, supersetId) => {
    setPlanData(prev => ({
      ...prev,
      exercises: prev?.exercises?.map(ex => 
        ex?.id === exerciseId 
          ? {
              ...ex,
              planConfig: {
                ...ex?.planConfig,
                supersetGroup: supersetId,
                supersetOrder: 1
              }
            }
          : ex
      ),
      updatedAt: new Date()
    }));
  };

  const handleSupersetUpdate = (supersetId, updates) => {
    setPlanData(prev => ({
      ...prev,
      exercises: prev?.exercises?.map(ex => 
        ex?.planConfig?.supersetGroup === supersetId 
          ? { ...ex, ...updates }
          : ex
      ),
      updatedAt: new Date()
    }));
  };

  const handleSavePlan = (planToSave = planData) => {
    // Simulate saving to backend
    const savedPlan = {
      ...planToSave,
      id: planToSave?.id || `plan-${Date.now()}`,
      updatedAt: new Date()
    };

    console.log('Saving plan:', savedPlan);
    
    // Show success message
    alert(`Training plan "${savedPlan?.name}" has been saved successfully!`);
    
    // Navigate to client management or trainer dashboard
    navigate('/client-management');
  };

  const handlePreviewPlan = () => {
    if (planData?.exercises?.length === 0) {
      alert('Please add at least one exercise to preview the plan.');
      return;
    }
    setShowPreview(true);
  };

  const handleNewPlan = () => {
    if (planData?.exercises?.length > 0) {
      const confirmed = window.confirm('Are you sure you want to create a new plan? All current progress will be lost.');
      if (!confirmed) return;
    }
    
    setPlanData({
      id: null,
      name: '',
      description: '',
      assignedClient: '',
      defaultPhase: 'phase-1',
      autoProgression: true,
      adaptiveIntensity: true,
      exercises: [],
      createdAt: new Date(),
      updatedAt: new Date()
    });
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Sidebar */}
      <TrainerSidebar 
        isCollapsed={sidebarCollapsed} 
        onToggle={() => setSidebarCollapsed(!sidebarCollapsed)} 
      />
      {/* Header */}
      <SessionMonitorHeader sidebarCollapsed={sidebarCollapsed} />
      {/* Main Content */}
      <main 
        className={`
          pt-16 transition-all duration-300 ease-out
          ${sidebarCollapsed ? 'ml-20' : 'ml-80'}
          lg:${sidebarCollapsed ? 'ml-20' : 'ml-80'}
          md:${sidebarCollapsed ? 'ml-16' : 'ml-72'}
          sm:${sidebarCollapsed ? 'ml-16' : 'ml-64'}
        `}
      >
        <div className="p-6 h-[calc(100vh-4rem)] mt-4">
          {/* Page Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl font-bold text-foreground">Training Plan Builder</h1>
              <p className="text-muted-foreground">
                Create adaptive training programs with drag-and-drop functionality
              </p>
            </div>
            
            <div className="flex items-center space-x-3">
              <Button
                variant="outline"
                onClick={handleNewPlan}
                iconName="Plus"
                className="breathing-focus"
              >
                New Plan
              </Button>
              
              <Button
                variant="secondary"
                onClick={handlePreviewPlan}
                iconName="Eye"
                className="breathing-focus"
                disabled={planData?.exercises?.length === 0}
              >
                Preview
              </Button>
              
              <Button
                variant="default"
                onClick={() => handleSavePlan()}
                iconName="Save"
                className="breathing-focus haptic-button"
                disabled={!planData?.name?.trim() || planData?.exercises?.length === 0}
              >
                Save Plan
              </Button>
            </div>
          </div>

          {/* Mobile Tab Navigation */}
          {isMobile && (
            <div className="flex bg-muted rounded-lg p-1 mb-6">
              <button
                onClick={() => setActiveTab('exercises')}
                className={`
                  flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors
                  ${activeTab === 'exercises' ?'bg-surface text-foreground shadow-sm' :'text-muted-foreground hover:text-foreground'
                  }
                `}
              >
                <Icon name="Database" size={16} className="mr-2 inline" />
                Exercise Database
              </button>
              <button
                onClick={() => setActiveTab('builder')}
                className={`
                  flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors
                  ${activeTab === 'builder' ?'bg-surface text-foreground shadow-sm' :'text-muted-foreground hover:text-foreground'
                  }
                `}
              >
                <Icon name="FileText" size={16} className="mr-2 inline" />
                Plan Builder
              </button>
            </div>
          )}

          {/* Content Layout */}
          <div className="h-[calc(100%-8rem)] flex gap-6">
            {/* Desktop: Split Layout, Mobile: Tabbed */}
            {!isMobile ? (
              <>
                {/* Exercise Database Panel */}
                <div className="w-1/2 h-full">
                  <ExerciseDatabase
                    searchTerm={searchTerm}
                    onSearchChange={setSearchTerm}
                    selectedCategory={selectedCategory}
                    onCategoryChange={setSelectedCategory}
                    onDragStart={() => {}}
                  />
                </div>

                {/* Plan Builder Panel */}
                <div className="w-1/2 h-full">
                  <PlanBuilder
                    planData={planData}
                    onPlanUpdate={setPlanData}
                    onSave={handleSavePlan}
                    onExerciseUpdate={handleExerciseUpdate}
                    onExerciseRemove={handleExerciseRemove}
                    onSupersetCreate={handleSupersetCreate}
                    onSupersetUpdate={handleSupersetUpdate}
                  />
                </div>
              </>
            ) : (
              <div className="w-full h-full">
                {activeTab === 'exercises' ? (
                  <ExerciseDatabase
                    searchTerm={searchTerm}
                    onSearchChange={setSearchTerm}
                    selectedCategory={selectedCategory}
                    onCategoryChange={setSelectedCategory}
                    onDragStart={() => {}}
                  />
                ) : (
                  <PlanBuilder
                    planData={planData}
                    onPlanUpdate={setPlanData}
                    onSave={handleSavePlan}
                    onExerciseUpdate={handleExerciseUpdate}
                    onExerciseRemove={handleExerciseRemove}
                    onSupersetCreate={handleSupersetCreate}
                    onSupersetUpdate={handleSupersetUpdate}
                  />
                )}
              </div>
            )}
          </div>
        </div>
      </main>
      {/* Plan Preview Modal */}
      {showPreview && (
        <PlanPreview
          planData={planData}
          onClose={() => setShowPreview(false)}
          onSave={() => {
            setShowPreview(false);
            handleSavePlan();
          }}
        />
      )}
    </div>
  );
};

export default TrainingPlanBuilder;